// frontend/src/components/onboarding/PracticeAreaStep.tsx
import React, { useEffect, useState } from 'react';
import { Scale, Loader2, CheckCircle2 } from 'lucide-react';
import { getPracticeAreas, type PracticeAreaOption } from '../../services/metaApi';
import type { PracticeArea } from '../../types/practiceArea';
import {
  PRACTICE_AREA_ROADMAP,
  type PracticeAreaRoadmapEntry,
} from './practiceAreaRoadmap';

interface Props {
  /** Pre-selected primary area (e.g. from the authed user's saved value). */
  initialPrimary?: PracticeArea | null;
  /**
   * Called on Continue with the chosen practice area as a single-element list.
   * Onboarding is single-select: a lawyer picks ONE live vertical. Additional
   * verticals are separate subscriptions added later from Settings/billing —
   * they are not multi-selected here. The list shape (rather than a bare
   * PracticeArea) is kept so the signup payload contract (`practice_areas`)
   * is unchanged.
   */
  onSelect: (areas: PracticeArea[]) => void;
  loading?: boolean;
}

/** "Available now" badge. Only shipped verticals reach this component now, so
 * this is the only badge state — coming-soon verticals are filtered out of
 * onboarding entirely (they'd only confuse an advocate who can't pick them). */
const AvailableBadge: React.FC = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-legal-green-100 dark:bg-legal-green-900/30 text-legal-green-700 dark:text-legal-green-300 text-[11px] font-semibold uppercase tracking-wide">
    <CheckCircle2 className="h-3 w-3" />
    Available now
  </span>
);

const PracticeAreaStep: React.FC<Props> = ({ initialPrimary, onSelect, loading }) => {
  // metaApi gives us the backend's practice-area list. We only render verticals
  // that are BOTH shipped (roadmap status 'available') AND backend-known — so a
  // vertical can never appear here without a working product behind it. The API
  // list is fetched to confirm availability, but display copy/order comes from
  // the roadmap.
  const [apiOptions, setApiOptions] = useState<PracticeAreaOption[] | null>(null);

  // Single-select: null until the lawyer picks one. Continue stays disabled
  // while null (the step is non-skippable).
  // With a single practice on sale (criminal-only launch) it is preselected —
  // a one-card choice the lawyer must click is friction, not a decision.
  const onSale = PRACTICE_AREA_ROADMAP.filter((e) => e.status === 'available');
  const [primary, setPrimary] = useState<PracticeArea | null>(
    initialPrimary && onSale.some((e) => e.value === initialPrimary)
      ? initialPrimary
      : onSale.length === 1
        ? onSale[0].value
        : null,
  );

  useEffect(() => {
    getPracticeAreas()
      .then(setApiOptions)
      // Fail OPEN, not empty: a /meta outage must not blank onboarding. Leaving
      // apiOptions null keeps the "trust the roadmap" path (below), so the
      // on-sale cards still render — single-vertical onboarding makes this the
      // ONLY path, so an empty list here would hard-block every new signup.
      .catch(() => setApiOptions(null));
  }, []);

  // Only the shipped ('available') verticals are offered. Coming-soon areas are
  // intentionally NOT rendered — showing an advocate a vertical they can't use
  // (or that needs a separate subscription that doesn't exist yet) is pure
  // confusion on the cold-start screen. New verticals appear here automatically
  // once their roadmap entry flips to 'available'.
  const availableList: PracticeAreaRoadmapEntry[] = React.useMemo(() => {
    const shipped = PRACTICE_AREA_ROADMAP.filter((e) => e.status === 'available');
    // Defensive: if the backend hasn't yet exposed a roadmap-available value,
    // don't offer it (avoids a card whose selection the backend would reject).
    if (apiOptions === null) return shipped; // pre-fetch: trust the roadmap
    const apiValues = new Set(apiOptions.map((o) => o.value));
    return shipped.filter((e) => apiValues.has(e.value));
  }, [apiOptions]);

  const handleSubmit = () => {
    if (!primary) return;
    // Single-element list — the primary IS the whole selection (see onSelect doc).
    onSelect([primary]);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-3">
        <Scale className="h-6 w-6 text-legal-green-500" />
        <h2 className="text-xl font-serif font-semibold text-text-primary dark:text-white">
          What&apos;s your practice area?
        </h2>
      </div>
      <p className="text-sm text-text-secondary mb-5">
        iAdvocate is live for criminal practice today.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {availableList.map((entry) => {
          const isSelected = primary === entry.value;
          return (
            <button
              key={entry.value}
              type="button"
              onClick={() => setPrimary(entry.value)}
              className={[
                'text-left rounded-xl border p-3 transition-colors',
                isSelected
                  ? 'border-legal-green-500 bg-legal-green-50 dark:bg-legal-green-900/20 ring-2 ring-legal-green-400/40'
                  : 'border-border-primary bg-surface hover:border-legal-green-400/50',
              ].join(' ')}
              aria-pressed={isSelected}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <span className="text-sm font-semibold text-text-primary dark:text-white">
                  {entry.label}
                </span>
                <AvailableBadge />
              </div>
              {entry.tagline && (
                <p className="text-xs text-text-tertiary mb-1.5 leading-snug">
                  {entry.tagline}
                </p>
              )}
              <span className="text-[11px] text-legal-green-700 dark:text-legal-green-300 font-medium">
                ✓ Pick this to start using iAdvocate today
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleSubmit}
        disabled={loading || !primary}
        className="w-full px-10 py-3 bg-legal-green-500 text-white font-semibold rounded-xl hover:bg-legal-green-400 focus:outline-none focus:ring-2 focus:ring-legal-green-500 focus:ring-offset-2 dark:focus:ring-offset-navy-950 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-base shadow-elevation-1 dark:shadow-dark-elevation-1"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </span>
        ) : (
          'Continue'
        )}
      </button>
    </div>
  );
};

export default PracticeAreaStep;
